import { Injectable } from '@angular/core';
import { INotificationContext, INotificationContextItem } from 'src/app/models/notifications';
import { NotificationEndpoint } from './notification-endpoint.service.ts';
import { BehaviorSubject, filter, first, Subject } from 'rxjs';
import { ConfigurationService } from "../configuration/configuration.service";
import { DataAction, Payload, Persistence, StateRepository } from '@angular-ru/ngxs/decorators';
import { State, Store } from '@ngxs/store';
import { NgxsDataRepository } from '@angular-ru/ngxs/repositories';
import produce from 'immer';
import { FeatureService } from 'src/app/shared/preview-feature/preview-feature.service';
import * as moment from 'moment';
import { ConfigsService } from '../configs/configs.service';

export interface INotificationSettings {
    type: string;
    color: string;
    date: Date;
}

export interface INotificationSettingsModel {
    loadedNotificationSettings: INotificationSettings[];
    notificationContextItems: INotificationContext[];
    lastChanged?: Date;
}

@Persistence()
@StateRepository()
@State<INotificationSettingsModel>({
  name: 'notificationSettingsState',
  defaults: {
    loadedNotificationSettings:  [],
    notificationContextItems: [],
    lastChanged: new Date(new Date().setFullYear(new Date().getFullYear() - 1))
  }
})
@Injectable()
export class NotificationService extends NgxsDataRepository<INotificationSettingsModel> {

  public notificationSubject = new BehaviorSubject<any>(null);

  readonly markedRead: string = "READ";
  readonly markedUnRead: string = "UNREAD";
  readonly markedDismiss: string = "DISMISS";

  scopeUser: string = "fa-light fa-user";
  scopeTeam: string = "fa-light fa-people-group";
  scopeMember: string = "fa-light fa-person";
  scopeAll: string = "fa-light fa-tower-broadcast";

  private loadInterval: number = 2400000;
  public notificationContext = new Subject<INotificationContext[]>();
  public notification$ = new Subject<INotificationContextItem[]>();

  public notificationItems: INotificationContextItem[] = [];
  public notificationContextItems: INotificationContext[] = [];
  notificationRequestInProgress: boolean;
  monthsAgo: Date;

  constructor(
    private readonly notificationEndpoint: NotificationEndpoint,
    private readonly store: Store,
    private readonly configurationService: ConfigurationService,
    private readonly featureService: FeatureService,
    private readonly configsService: ConfigsService
    ) {
    super();
  }

  async ngxsAfterBootstrap() {
    super.ngxsAfterBootstrap();

    this.notificationContext.subscribe(notification => {

      if (notification.length > 0) {
        this.loadnotificationContext(notification);
      }
    });

    this.ValidateStateStructureAndResetIfNeeded();
    await this.getMonthsAgoDateAndInitialize();

    await this.store.select(ConfigurationService.notificationLoadIntervalMinutesSelector).pipe(filter(_ => !!_),first()).toPromise();
    console.log("Loading Notifications", this.configurationService.notificationLoadIntervalMilliseconds);

    await this.featureService.initializeFeature("notification-cache");

    setInterval(async () => {

      if(!this.featureService.hasFeature("notification-cache"))
      {
        console.log("NOTIFICATION REFRESH NO CACHE");
        const context = await this.getNotificationContext({ LastRequestTime: this.monthsAgo });

        context.forEach(element => {
          element?.items?.forEach(item => {
            item.itemMessage = this.parseMessage(item.message);
            if (!item.dateProcessed) {
              item.createdOn = this.getDate(item.createdOn);
              item.dateProcessed = true;
            }
          })
        });

        this.notificationContext.next(context);
        this.notificationContextItems = context;
        this.setContextCache();
      }
      else {
        console.log("NOTIFICATION REFRESH WITH CACHE");
        const latestCreatedOnChangeDate = this.getNotificationContextDate('latest');
        const context = await this.getNotificationContext({ LastRequestTime: latestCreatedOnChangeDate });

        context.forEach(element => {
          element?.items?.forEach(item => {
            item.itemMessage = this.parseMessage(item.message);
            // if the date has not been processed, convert it to a Date object and make it UTC time
            if (!item.dateProcessed) {
              item.createdOn = this.getDate(item.createdOn);
              item.dateProcessed = true;
            }
          })
        });

        context.forEach(ctxElement => {
          const match = this.notificationContextItems.find(item => item.tenantId === ctxElement.tenantId);
          if (match && ctxElement.items) {
            ctxElement.items.forEach(newItem => {
              if (!match.items.some(existing => existing.id === newItem.id)) {
                match.items.push(newItem);
              }
            });
          }
        });

        this.notificationContext.next(this.notificationContextItems);
        this.setContextCache();
      }
    }, this.configurationService.notificationLoadIntervalMilliseconds || this.loadInterval);
  }

  async getMonthsAgoDateAndInitialize() {

    let configReview = await this.configsService.getKeys("NOTIFICATION_LOOKBACK_MONTHS");
    if(configReview && configReview.length > 0)
    {
      this.monthsAgo = new Date();
      this.monthsAgo.setMonth(this.monthsAgo.getMonth() - (configReview[0].Expression ?? 12));
    }
  }

  ValidateStateStructureAndResetIfNeeded() {

    // Dynamically get the default state structure from the State decorator
    const stateDefaults = (this.constructor as any).NGXS_OPTIONS?.defaults as INotificationSettingsModel
      ?? {
      loadedNotificationSettings: [],
      notificationContextItems: [],
      lastChanged: new Date(new Date().setFullYear(new Date().getFullYear() - 1))
      };

    // Check if the store snapshot structure is different than the current notificationSettingsState structure
    const currentSnapshot = this.store.selectSnapshot<INotificationSettingsModel>(state => state.notificationSettingsState);

    const keysMatch =
      Object.keys(currentSnapshot ?? {}).sort().join(',') ===
      Object.keys(stateDefaults).sort().join(',');

    if (!keysMatch) {
      // Remove/reset the notificationSettingsState from the store
      this.store.reset({...this.store.snapshot(), notificationSettingsState: { ...stateDefaults } });
      console.log('notificationSettingsState structure was different and has been reset to default.');
    }
  }

  parseMessage(message: string): Array<{type: 'text'|'button', value?: string, content?: string}>  {

    if(message?.trim().startsWith('[') && message?.trim().endsWith(']')) {
      try {
        const parsed = JSON.parse(message);
        if (Array.isArray(parsed)) {
          return parsed;
        } else {
          return [{ type: parsed.type, content: parsed }];
        }
      } catch (e) {
        console.error("Error parsing message:", e);
      }
    }
    else{
        return [{ type: 'text', value: message }];
    }
  }

  getNotificationItem(id: number) {
    return this.notificationItems.find(itm => itm.id === id);
  }

  async getWorkAssignedItems(request: any) {

    await this.getMonthsAgoDateAndInitialize();

    if(!this.featureService.hasFeature("notification-cache"))
    {
      console.log("NOTIFICATION NO CACHE");
      if(this.notificationRequestInProgress) {
        if(this.notificationContextItems.length > 0) {
          this.notificationContext.next(this.notificationContextItems);
        }
        return;
      }
      this.notificationRequestInProgress = true;

      const context = await this.getNotificationContext(Object.keys(request).length === 0 ? { LastRequestTime: this.monthsAgo } : request);

      context.forEach(element => {
        element?.items?.forEach(item => {
          item.itemMessage = this.parseMessage(item.message);

          // if the date has not been processed, convert it to a Date object and make it UTC time
          if (!item.dateProcessed) {
            item.createdOn = this.getDate(item.createdOn);
            item.dateProcessed = true;
          }
        })
      });

      this.notificationContext.next(context);
      this.notificationContextItems = context;
      this.setContextCache();
    }
    else
    {
      console.log("NOTIFICATION WITH CACHE");
      if(this.notificationRequestInProgress) {
        return;
      }
      this.notificationRequestInProgress = true;

      this.notificationContextItems = this.getContextCache();
      const lastChangeCacheDate = this.getNotificationContextDate('cache') || new Date(new Date().setFullYear(new Date().getFullYear() - 1));
      const today = new Date();

      // Compare only the date part (year, month, day)
      const isSameDay = lastChangeCacheDate.getFullYear() === today.getFullYear() &&
      lastChangeCacheDate.getMonth() === today.getMonth() &&
      lastChangeCacheDate.getDate() === today.getDate();

      if (!isSameDay) {
        const context = await this.getNotificationContext(Object.keys(request).length === 0 ? { LastRequestTime: this.monthsAgo } : request);
        context.forEach(element => {
          element?.items?.forEach(item => {
            item.itemMessage = this.parseMessage(item.message);
            if (!item.dateProcessed) {
              item.createdOn = this.getDate(item.createdOn);
              item.dateProcessed = true;
            }
         })
        });
        this.notificationContext.next(context);
        this.notificationContextItems = context;
        this.setContextCache();
      } else {

        const latestCreatedOnChangeDate = this.getLatestCreatedOnDate();
        const context = await this.getNotificationContext({ LastRequestTime: latestCreatedOnChangeDate });

        context.forEach(element => {
          element?.items?.forEach(item => {
            item.itemMessage = this.parseMessage(item.message);

            // if the date has not been processed, convert it to a Date object and make it UTC time
            if (!item.dateProcessed) {
              item.createdOn = this.getDate(item.createdOn);
              item.dateProcessed = true;
            }
          })
        });

        context.forEach(ctxElement => {
          const match = this.notificationContextItems.find(item => item.tenantId === ctxElement.tenantId);
          if (match && ctxElement.items) {
            ctxElement.items.forEach(newItem => {
              if (!match.items?.some(existing => existing.id === newItem.id)) {
                match.items?.push(newItem);
              }
            });
          }
        });

        this.notificationContext.next(this.notificationContextItems);
        this.setContextCache();
      }
    }
  }

  // Convert to Date object and ensure it's in UTC
  getDate(date: Date): Date {
      const dateMoment = moment(date, 'YYYY-MM-DD HH:mm:ss.SSS');
      return moment.utc(dateMoment.format('YYYY-MM-DD HH:mm:ss.SSS')).toDate();
  }

  public getNotificationContext(request: any): Promise<any> {
    return this.notificationEndpoint.getNotificationContext(request);
  }

  async dismissNotificationItem(item: INotificationContextItem): Promise<boolean> {

    item.status = this.markedDismiss;
    item.active = false;

    if(this.featureService.hasFeature("notification-cache"))
    {
      // Remove the item from the context's items array if it matches the id and tenant
      this.notificationContextItems?.forEach(ctx => {
        if (ctx.tenantId === item.tenant && Array.isArray(ctx.items)) {
          const idx = ctx.items.findIndex(i => i.id === item.id);
          if (idx !== -1) {
            ctx.items.splice(idx, 1);
          }
        }
      });
    }

    return await this.setNotificationStatus(item);
  }

  async readNotificationItem(item: INotificationContextItem): Promise<boolean> {

    item.status = "READ";
    return await this.setNotificationStatus(item);
  }

  async setNotificationStatus(item: INotificationContextItem): Promise<boolean> {

    let result = await this.notificationEndpoint.setNotificationItem(item.tenant, item.id, item.status);
    if (result) {
      this.notification$.next(this.notificationItems);
    }
    this.setContextCache();
    return result;
  }

  public loadnotificationContext(notification: INotificationContext[]) {

    this.notificationItems = [];

    if (notification.length > 0) {
      notification.forEach(element => {

        if (element?.items?.length > 0) {

          this.processElement(element);
        }

        if (element.items) {
          this.notificationItems.push(...element.items);
        }

        this.notification$.next(this.notificationItems);
      });
    }
  }

  processElement(element: INotificationContext) {

    element.items.forEach(itm => {

      itm.tenant = element.tenantId;
      itm.tenantDisplayName = element.tenantDisplayName;
      itm.from = itm.createdBy?.displayName ? itm.createdBy?.displayName : itm.createdBy;
      itm.active = true;

      switch (itm.scopeType) {
        case "USER":
          {
            itm.scopeIcon = this.scopeUser;
            break;
          }

        case "TEAM":
          {
            itm.scopeIcon = this.scopeTeam;
            break;
          }

        case "MEMBER":
          {
            itm.scopeIcon = this.scopeMember;
            break;
          }

        case "ALL":
          {
            itm.scopeIcon = this.scopeAll;
            break;
          }
      }
    });
  }

  //////////////////////////////////////////////////////////
  // get the date to be used for this request based on the parameter pssed in
  public getNotificationContextDate(param: string): Date | null {
    switch (param) {
      case 'latest':
        return this.getLatestCreatedOnDate();
      case 'cache':
        return this.getContextCacheDate();
      default:
        return null;
    }
  }

  ////////////////////////////////////////////////////////////////////
  public getNotificationSettings()  {
     return this.snapshot.loadedNotificationSettings || [];
  }

  ////////////////////////////////////////////////////////////////
  public setContextCache() {
    this.setNotificationSettings(null);
  }

  public getContextCache() {
    // Use JSON.parse/stringify with a custom replacer to preserve Date objects with seconds
    return JSON.parse(JSON.stringify(this.snapshot.notificationContextItems || [], (key, value) => {
      // Convert Date objects to ISO string (preserves seconds)
      if (value instanceof Date) {
        console.log("SERIALIZING DATE", key, value);
        return value.toISOString();
      }
      return value;
    })).map((ctx: any) => {
      // Convert date strings back to Date objects
      if (ctx.items) {
        console.log("DESERIALIZING CONTEXT ITEMS", ctx.items);
        ctx.items = ctx.items.map((item: any) => {
          if (item.createdOn && typeof item.createdOn === 'string') {
            item.createdOn = new Date(item.createdOn);
          }
          return item;
        });
      }
      return ctx;
    });
  }

  public getContextCacheDate() {
    return new Date(this.snapshot.lastChanged || new Date(new Date().setFullYear(new Date().getFullYear() - 1)));
  }

  public getLatestCreatedOnDate(): Date {

    let latest: Date = new Date(new Date().setFullYear(new Date().getFullYear() - 1));
    this.notificationContextItems?.forEach(ctx => {
      ctx.items?.forEach(item => {
        if (item.createdOn) {
            const createdOnDate = typeof item.createdOn === 'string' ? new Date(item.createdOn) : item.createdOn;
            if (!latest || createdOnDate.getTime() > latest.getTime()) {
              latest = createdOnDate;
            }
        }
      });
    });
    if (latest) {
      const newDate = new Date(latest);
      newDate.setMilliseconds(newDate.getMilliseconds() + 1);
      return newDate;
    }
    return new Date(new Date().setFullYear(new Date().getFullYear() - 1));
  }

  /////////////////////////////////////////////////////////////////

  @DataAction()
  public setNotificationSettings(@Payload('notification') newSettings: INotificationSettings) {

    this.ctx.setState(produce(this.ctx.getState(), (draft: INotificationSettingsModel) => {

      if(newSettings == null) {
        draft.notificationContextItems = this.cloneNotification(this.notificationContextItems);;
        draft.lastChanged = new Date();
        return;
      }

      draft.notificationContextItems = this.cloneNotification(this.notificationContextItems);
      // Check if the new history item already exists
       const existingIndex = draft.loadedNotificationSettings.findIndex(m =>
           m.type === newSettings.type
       );

      // If it exists, update the date; otherwise, add a new item
      if (existingIndex > -1) {
          draft.loadedNotificationSettings[existingIndex].color = newSettings.color;
      } else {
        draft.loadedNotificationSettings.push({ ...newSettings });
      }

    }))
  }

  cloneNotification<T>(obj: T, replacer: (key: string, value: any) => any = undefined): T {
      if (!!obj) {
          return JSON.parse(JSON.stringify(obj, this.getCircularReplacer())) as T;
      }

      return obj;
  }

  getCircularReplacer() {
    const seen = new WeakSet();
    return function(key: string, value: any) {
      // Skip functions and observables (or any custom logic)
      if (typeof value === 'function') return undefined;
      // Skip circular references
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) return undefined;
        seen.add(value);
      }
      // Ensure createdOn Date is serialized with milliseconds
      if (key === 'createdOn' && value instanceof Date) {
        return value.toISOString(); // ISO string includes milliseconds
      }
      return value;
    };

  }
}
